import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FrontendGuest } from '@/services/transform';
import RichTextRenderer from '@/components/base/RichTextRenderer';

interface GuestDetailModalProps {
  guest: FrontendGuest | null;
  onClose: () => void;
  allGuests?: FrontendGuest[];
  onSelectGuest?: (g: FrontendGuest) => void;
}

export default function GuestDetailModal({ guest, onClose, allGuests = [], onSelectGuest }: GuestDetailModalProps) {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  useEffect(() => {
    if (!guest) return;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [guest, onClose]);

  if (!guest) return null;

  // Compute similar guests by expertise overlap (exclude self) - with deduplication
  const similarGuests = useMemo(() => {
    const seen = new Set<string>();
    return allGuests
      .filter((g) => g.id !== guest.id)
      .map((g) => {
        const guestExpertiseEn = new Set(guest.expertiseEn.map((e) => e.toLowerCase()));
        const overlap = g.expertiseEn.filter((e) => guestExpertiseEn.has(e.toLowerCase())).length;
        return { guest: g, overlap };
      })
      .filter((item) => item.overlap > 0)
      .sort((a, b) => b.overlap - a.overlap)
      .filter((item) => {
        if (seen.has(item.guest.id)) return false;
        seen.add(item.guest.id);
        return true;
      })
      .slice(0, 3)
      .map((item) => item.guest);
  }, [allGuests, guest]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      
      {/* Poster Container - 固定结构，照片固定，内容滚动 */}
      <div className="relative w-full max-w-6xl max-h-[90vh] md:max-h-[85vh] h-[95vh] md:h-[85vh] overflow-hidden rounded-3xl shadow-2xl bg-white flex flex-col md:flex-row">
        
        {/* 左侧：照片展示区域 - 固定高度，不滚动 */}
        <div className="w-full md:w-2/5 h-48 md:h-full bg-gradient-to-br from-[#0A2540] via-[#1a3a5c] to-[#2C7DA0] relative flex-shrink-0">
          {/* 照片容器 - 完整显示，固定高度 */}
          <div className="absolute inset-0 flex items-center justify-center p-4 md:p-6">
            {guest.photo ? (
              <img
                src={guest.photo}
                alt={isZh ? guest.name : guest.nameEn}
                className="max-w-full max-h-full object-contain object-center"
                style={{
                  filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))'
                }}
              />
            ) : (
              <div className="text-center">
                <div className="w-24 h-24 md:w-32 md:h-32 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-3 md:mb-4">
                  <i className="ri-user-line text-4xl md:text-5xl text-white/30" />
                </div>
                <p className="text-white/40 text-sm">{isZh ? guest.name : guest.nameEn}</p>
              </div>
            )}
          </div>
          
          {/* 底部渐变遮罩 - 仅在小屏幕显示 */}
          <div className="absolute bottom-0 left-0 right-0 h-16 md:h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        </div>

        {/* 右侧：详细信息区域 - 可滚动 */}
        <div className="w-full md:w-3/5 bg-white flex flex-col flex-1 min-w-0 overflow-hidden">
          {/* 顶部渐变条 */}
          <div className="h-1.5 bg-gradient-to-r from-[#0A2540] to-[#2C7DA0] flex-shrink-0" />
          
          {/* 可滚动的内容区域 */}
          <div className="flex-1 overflow-y-auto p-5 md:p-7">
            {/* 基本信息 */}
            <div className="mb-5">
              <h2 className="text-xl md:text-3xl font-bold text-[#0A2540] mb-1.5 leading-tight">
                {isZh ? guest.name : guest.nameEn}
              </h2>
              <p className="text-[#2C7DA0] text-base md:text-lg font-medium mb-1">
                {isZh ? guest.title : guest.titleEn}
              </p>
              <p className="text-[#0A2540]/60 text-sm md:text-base">
                {isZh ? guest.institution : guest.institutionEn}
              </p>
              
              {/* 快速信息标签 */}
              <div className="flex flex-wrap gap-2 mt-3">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F7F9FC] text-[#0A2540]/70 text-xs">
                  <i className="ri-map-pin-line text-[#2C7DA0]" />
                  <span>{isZh ? guest.country : guest.countryEn}</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs">
                  <i className="ri-calendar-line" />
                  <span>{guest.year}</span>
                </div>
              </div>
            </div>

            {/* 分隔线 */}
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px flex-1 bg-gradient-to-r from-[#2C7DA0]/30 to-transparent" />
              <div className="w-2 h-2 rounded-full bg-[#2C7DA0]/50" />
              <div className="h-px flex-1 bg-gradient-to-l from-[#2C7DA0]/30 to-transparent" />
            </div>

            {/* 研究领域 */}
            <div className="mb-5">
              <h3 className="text-[#0A2540] font-bold text-xs md:text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center bg-[#2C7DA0]/10 rounded">
                  <i className="ri-focus-3-line text-[#2C7DA0] text-xs" />
                </div>
                <span>{t('guestsPage.modal.expertise')}</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {(isZh ? guest.expertise : guest.expertiseEn).map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-[#2C7DA0]/10 text-[#2C7DA0] rounded-full text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* 个人简介 */}
            <div className="mb-5">
              <h3 className="text-[#0A2540] font-bold text-xs md:text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center bg-[#2C7DA0]/10 rounded">
                  <i className="ri-user-3-line text-[#2C7DA0] text-xs" />
                </div>
                <span>{t('guestsPage.modal.bio')}</span>
              </h3>
              <div className="bg-[#F7F9FC] rounded-xl p-4 md:p-5 max-h-[250px] md:max-h-[350px] overflow-y-auto">
                <RichTextRenderer
                  content={isZh ? guest.bio : guest.bioEn}
                  className="text-[#0A2540]/70 text-sm leading-relaxed"
                />
              </div>
            </div>

            {/* 奖项 */}
            {guest.awards && guest.awards.length > 0 && (
              <div className="mb-5">
                <h3 className="text-[#0A2540] font-bold text-xs md:text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#2C7DA0]/10 rounded">
                    <i className="ri-award-line text-[#2C7DA0] text-xs" />
                  </div>
                  <span>{t('guestsPage.modal.awards')}</span>
                </h3>
                <div className="space-y-2">
                  {(isZh ? (guest.awards ?? []) : (guest.awardsEn ?? [])).map((award, idx) => (
                    <div key={`${idx}-${award}`} className="flex items-start gap-3 bg-[#F7F9FC] rounded-lg p-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-star-fill text-amber-500 text-xs" />
                      </div>
                      <span className="text-[#0A2540]/70 text-sm">{award}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 相似嘉宾推荐 */}
            {similarGuests.length > 0 && (
              <div className="mb-5">
                <div className="h-px bg-[#0A2540]/8 mb-4" />
                <h3 className="text-[#0A2540] font-bold text-xs md:text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#2C7DA0]/10 rounded">
                    <i className="ri-user-shared-line text-[#2C7DA0] text-xs" />
                  </div>
                  <span>{t('guestsPage.modal.similar')}</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {similarGuests.map((sg) => {
                    const guestExpertiseEn = new Set(guest.expertiseEn.map((e) => e.toLowerCase()));
                    const sharedTags = (isZh ? sg.expertise : sg.expertiseEn).filter((_, idx) => {
                      const enTag = sg.expertiseEn[idx]?.toLowerCase();
                      return enTag && guestExpertiseEn.has(enTag);
                    });

                    return (
                      <button
                        key={sg.id}
                        onClick={() => onSelectGuest && onSelectGuest(sg)}
                        className="group flex flex-col items-center text-center p-4 bg-[#F7F9FC] rounded-xl hover:bg-[#0A2540]/5 transition-all cursor-pointer border border-transparent hover:border-[#0A2540]/10"
                      >
                        <div className="w-14 h-14 rounded-full overflow-hidden mb-2.5 ring-2 ring-transparent group-hover:ring-[#2C7DA0]/30 transition-all">
                          {sg.photo ? (
                            <img
                              src={sg.photo}
                              alt={isZh ? sg.name : sg.nameEn}
                              className="w-full h-full object-cover object-top"
                            />
                          ) : (
                            <div className="w-full h-full bg-[#0A2540]/10 flex items-center justify-center">
                              <i className="ri-user-line text-xl text-[#0A2540]/30" />
                            </div>
                          )}
                        </div>
                        <p className="font-semibold text-[#0A2540] text-xs leading-snug mb-1">
                          {isZh ? sg.name : sg.nameEn}
                        </p>
                        <p className="text-[#0A2540]/45 text-[10px] mb-2 line-clamp-1">
                          {isZh ? sg.institution : sg.institutionEn}
                        </p>
                        {sharedTags.length > 0 && (
                          <span className="px-2 py-0.5 bg-[#2C7DA0]/10 text-[#2C7DA0] rounded-full text-[10px] font-medium line-clamp-1 max-w-full">
                            {sharedTags[0]}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 操作按钮 */}
            <div className="flex justify-center pt-4 border-t border-[#0A2540]/8">
              {guest.linkedin && (
                <a
                  href={guest.linkedin}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg text-sm font-medium transition-all group"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-linkedin-fill" />
                  </div>
                  <span>{isZh ? '查看 LinkedIn' : 'View LinkedIn'}</span>
                  <div className="w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-external-link-line text-xs" />
                  </div>
                </a>
              )}
            </div>
          </div>
          
          {/* 底部渐变条 */}
          <div className="h-1 bg-gradient-to-r from-[#2C7DA0] to-[#0A2540] flex-shrink-0" />
        </div>

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all backdrop-blur-sm cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl" />
        </button>

        {/* 装饰角标 */}
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-[#2C7DA0]/10 to-transparent rounded-tr-full" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#7EC8E3]/10 to-transparent rounded-tl-full" />
      </div>
    </div>
  );
}
